import { forgotPassword } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/auth/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { parseAuthError } from "@/utils/auth-errors";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";

interface FormData {
    email: string;
}

const ForgotPassword = () => {
    const { authenticated } = useAuth();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({ defaultValues: { email: "" } });

    const { mutate, isPending, isSuccess, reset, variables, error } =
        useMutation({
            mutationFn: forgotPassword,
        });

    const serverError = useMemo(
        () => (error ? parseAuthError(error) : null),
        [error]
    );

    useDocumentTitle("Forgot password");

    useEffect(() => {
        if (authenticated) {
            navigate("/");
        }
    }, [authenticated, navigate]);

    if (isSuccess) {
        return (
            <div className="space-y-7" role="status" aria-live="polite">
                <div>
                    <p className="text-muted-foreground mb-2.5 text-[11px] font-medium tracking-[0.22em] uppercase">
                        Sent
                    </p>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-semibold tracking-tight">
                            Check your inbox
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            If{" "}
                            <span className="text-foreground font-medium">
                                {variables}
                            </span>{" "}
                            is registered, we've sent a reset link. It's good
                            for the next 30 minutes.
                        </p>
                    </div>
                </div>
                <div className="space-y-3">
                    <Button
                        type="button"
                        variant="outline"
                        className="h-10 w-full"
                        onClick={() => reset()}
                    >
                        Try a different email
                    </Button>
                    <Link
                        to="/login"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
                    >
                        <ArrowLeft className="size-3.5" />
                        Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit((data) => mutate(data.email))} noValidate>
            <div className="space-y-7">
                {/* Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-semibold tracking-tight">
                        Forgot your password?
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        We'll email you a link to reset it.
                    </p>
                </div>

                {/* Server error */}
                {serverError && (
                    <div
                        role="alert"
                        aria-live="polite"
                        className="border-destructive/30 bg-destructive/[0.06] text-destructive rounded-md border px-3 py-2.5 text-sm"
                    >
                        {serverError.message}
                    </div>
                )}

                {/* Fields */}
                <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        className="h-10"
                        autoComplete="email"
                        autoFocus
                        aria-invalid={!!errors.email}
                        {...register("email", {
                            required: "Email is required",
                            pattern: {
                                value: /^\S+@\S+\.\S+$/,
                                message: "Enter a valid email",
                            },
                        })}
                    />
                    {errors.email && (
                        <p className="text-destructive text-sm">
                            {errors.email.message}
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="space-y-4">
                    <Button
                        type="submit"
                        disabled={isPending}
                        className="h-10 w-full"
                    >
                        {isPending && <Spinner className="mr-2" />}
                        Send reset link
                    </Button>
                    <Link
                        to="/login"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
                    >
                        <ArrowLeft className="size-3.5" />
                        Back to sign in
                    </Link>
                </div>
            </div>
        </form>
    );
};

export default ForgotPassword;
